// The Pashmak API from the browser. Same endpoints as the apps; a signed-in
// session ("Authorization: Bearer …") stands in for the app token.
//
// Errors: 401 → signed out (session listeners fire), 402 → daily limit
// (limit listeners fire, so one global dialog handles every feature).

import type { Feature } from "./types";

export const API_BASE: string =
  (import.meta.env.VITE_API_BASE as string | undefined) ??
  (import.meta.env.DEV ? "/api" : "https://lm6ekfwmdcjrea4kbttdvflc3u0ciiql.lambda-url.us-east-1.on.aws");

const SESSION_KEY = "pashmak.session";

export class ApiError extends Error {
  constructor(public status: number, message: string, public body?: unknown) {
    super(message);
  }
}
export class LimitError extends ApiError {
  constructor(public feature: Feature, public limit: number, public tier: string) {
    super(402, "limit");
  }
}

// ---- session token ----

let session: string | null = read();
function read(): string | null {
  try {
    return localStorage.getItem(SESSION_KEY);
  } catch {
    return null;
  }
}
export function getSession() {
  return session;
}
export function setSession(token: string | null) {
  session = token;
  try {
    if (token) localStorage.setItem(SESSION_KEY, token);
    else localStorage.removeItem(SESSION_KEY);
  } catch {
    /* private mode: session lives for this tab only */
  }
}

type Listener<T> = (v: T) => void;
const signedOutListeners = new Set<Listener<void>>();
const limitListeners = new Set<Listener<LimitError>>();
export const onSignedOut = (f: Listener<void>) => (signedOutListeners.add(f), () => void signedOutListeners.delete(f));
export const onLimit = (f: Listener<LimitError>) => (limitListeners.add(f), () => void limitListeners.delete(f));

// ---- requests ----

async function raw(path: string, body: unknown, init: RequestInit = {}): Promise<Response> {
  const headers: Record<string, string> = { "content-type": "application/json" };
  if (session) headers.authorization = `Bearer ${session}`;
  let r: Response;
  try {
    r = await fetch(API_BASE + path, { method: "POST", headers, body: JSON.stringify(body ?? {}), ...init });
  } catch (e) {
    if ((e as Error).name === "AbortError") throw e;
    throw new ApiError(0, "You seem to be offline. Check your connection and try again.");
  }
  if (r.ok) return r;
  let data: any = null;
  try {
    data = await r.json();
  } catch {
    /* not JSON */
  }
  if (r.status === 401 && session) {
    setSession(null);
    signedOutListeners.forEach((f) => f());
  }
  if (r.status === 402 && data?.error === "limit") {
    const e = new LimitError(data.feature, data.limit, data.tier);
    limitListeners.forEach((f) => f(e));
    throw e;
  }
  const msg =
    data?.error && typeof data.error === "string" && r.status < 500
      ? data.error
      : r.status === 429
        ? "Slow down a little and try again."
        : "Pashmak hit a snag. Please try again.";
  throw new ApiError(r.status, msg, data);
}

export async function post<T>(path: string, body?: unknown, init?: RequestInit): Promise<T> {
  const r = await raw(path, body, init);
  return (await r.json()) as T;
}

export async function postBlob(path: string, body?: unknown, init?: RequestInit): Promise<Blob> {
  const r = await raw(path, body, init);
  return r.blob();
}

/** NDJSON stream (POST /chat/stream): yields each parsed line as it arrives. */
export async function* postStream<T>(path: string, body: unknown, signal?: AbortSignal): AsyncGenerator<T> {
  const r = await raw(path, body, { signal });
  if (!r.body) throw new ApiError(0, "Streaming isn't supported in this browser.");
  const reader = r.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    let nl;
    while ((nl = buf.indexOf("\n")) >= 0) {
      const line = buf.slice(0, nl).trim();
      buf = buf.slice(nl + 1);
      if (line) yield JSON.parse(line) as T;
    }
  }
  if (buf.trim()) yield JSON.parse(buf) as T;
}

/** Human message for any thrown error. */
export function errorMessage(e: unknown): string {
  if (e instanceof LimitError) return "That's today's free limit.";
  if (e instanceof ApiError) return e.message;
  return "Something went wrong. Please try again.";
}
